import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { 
  Box, 
  Paper, 
  Button, 
  Typography, 
  Stack, 
  Chip,
  Tooltip,
  IconButton,
  Divider,
  Alert,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Autocomplete,
  TextField,
  alpha
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  Today as TodayIcon,
  DateRange as DateRangeIcon,
  CalendarMonth as CalendarMonthIcon,
  TrendingUp as TrendingUpIcon,
  AllInclusive as AllIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { useAccount } from '../../contexts/AccountContext';
import { useNavigate } from 'react-router-dom';
import teamService from '../../services/teamService';
import { fetchAgentSelect } from '../../services/common';

dayjs.extend(isoWeek);

const getTimeFilterOptions = (showAllOption = true) => {
  const today = dayjs();
  const currentQuarter = Math.floor(today.month() / 3);
  const quarterStartMonth = currentQuarter * 3;
  const quarterStart = today.month(quarterStartMonth).startOf('month');
  const quarterEnd = today.month(quarterStartMonth + 2).endOf('month');

  const yesterday = today.subtract(1, 'day');
  const options = [
    {
      id: 'Today',
      label: 'Today',
      description: 'View today\'s data',
      icon: <TodayIcon fontSize="small" />,
      dateRange: today.format('DD/MM/YY'),
      color: '#17a2b8'  // Changed from green to blue (was All's color)
    },
    {
      id: 'Yesterday',
      label: 'Yesterday',
      description: 'View yesterday\'s data',
      icon: <TodayIcon fontSize="small" />,
      dateRange: yesterday.format('DD/MM/YY'),
      color: '#6c757d'
    },
    {
      id: 'This Week',
      label: 'This Week',
      description: 'View this week\'s data',
      icon: <DateRangeIcon fontSize="small" />,
      dateRange: `${today.startOf('isoWeek').format('DD/MM/YY')}-${today.endOf('isoWeek').format('DD/MM/YY')}`,
      color: '#007bff'
    },
    {
      id: 'This Month',
      label: 'This Month',
      description: 'View this month\'s data',
      icon: <CalendarMonthIcon fontSize="small" />,
      dateRange: `${today.startOf('month').format('DD/MM/YY')}-${today.endOf('month').format('DD/MM/YY')}`,
      color: '#fd7e14'
    },
    {
      id: 'This Quarter',
      label: 'This Quarter',
      description: 'View this quarter\'s data',
      icon: <TrendingUpIcon fontSize="small" />,
      dateRange: `${quarterStart.format('DD/MM/YY')}-${quarterEnd.format('DD/MM/YY')}`,
      color: '#6f42c1'
    }
  ];

  // Add "All" option at the beginning if showAllOption is true
  if (showAllOption) {
    options.unshift({
      id: 'All',
      label: 'All',
      description: 'View all results',
      icon: <AllIcon fontSize="small" />,
      dateRange: 'All results',
      color: '#28a745'  // Changed from blue to green (was Today's color)
    });
  }

  return options;
};

function getDateRanges(filter) {
  const today = dayjs().endOf('day');
  let currentStart, currentEnd, compareStart, compareEnd, compareLabel;
  const filterName = filter.id || filter;

  switch (filterName) {
    case 'All':
      // For "All", return null dates to indicate no date filtering
      currentStart = null;
      currentEnd = null;
      compareStart = null;
      compareEnd = null;
      compareLabel = 'all_results';
      break;
    case 'Today':
      currentEnd = today;
      currentStart = today.startOf('day');
      compareEnd = currentStart.subtract(1, 'day');
      compareStart = compareEnd.startOf('day');
      compareLabel = 'yesterday';
      break;
    case 'Yesterday':
      const yesterday = today.subtract(1, 'day');
      currentEnd = yesterday.endOf('day');
      currentStart = yesterday.startOf('day');
      compareEnd = currentStart.subtract(1, 'day');
      compareStart = compareEnd.startOf('day');
      compareLabel = 'day before yesterday';
      break;
    case 'This Week':
      currentEnd = today;
      currentStart = today.startOf('isoWeek');
      compareEnd = currentStart.subtract(1, 'day');
      compareStart = compareEnd.startOf('isoWeek');
      compareLabel = 'last week';
      break;
    case 'This Month':
      currentEnd = today;
      currentStart = today.startOf('month');
      compareEnd = currentStart.subtract(1, 'day');
      compareStart = compareEnd.startOf('month');
      compareLabel = 'last month';
      break;
    case 'This Quarter':
      const currentQuarter = Math.floor(today.month() / 3);
      const quarterStartMonth = currentQuarter * 3;
      currentStart = today.month(quarterStartMonth).startOf('month');
      currentEnd = today.month(quarterStartMonth + 2).endOf('month');
      const prevQuarterEnd = currentStart.subtract(1, 'day');
      const prevQuarterStartMonth = Math.floor(prevQuarterEnd.month() / 3) * 3;
      compareStart = prevQuarterEnd.month(prevQuarterStartMonth).startOf('month');
      compareEnd = prevQuarterEnd;
      compareLabel = 'last quarter';
      break;
    default:
      currentEnd = today;
      currentStart = today.subtract(7, 'day');
      compareEnd = currentStart;
      compareStart = currentStart.subtract(7, 'day');
      compareLabel = 'previous period';
  }
  return { currentStart, currentEnd, compareStart, compareEnd, compareLabel };
}

const DateRangeFilter = ({ 
  onChange, 
  showAllOption = true, 
  defaultFilterId = null,
  initialDateRange = null,
  isAgentReportsPage = false, 
  onTeamProjectFilter, 
  onAgentFilter,
  onExportPDF, 
  selectedTeams: externalSelectedTeams,
  selectedAgents: externalSelectedAgents,
  setTeams,
  setAgents,
  showTeamFilter = false,
  showAgentFilter = false,
  showExportPDF = false,
  hideTimeFilters = false 
}) => {
  const { accountId } = useAccount();
  const navigate = useNavigate();
  const timeFilterOptions = getTimeFilterOptions(showAllOption);
  
  const resolveDefaultFilter = () => {
    // If initialDateRange is provided (from localStorage), match it against timeFilterOptions
    if (initialDateRange?.filter?.id) {
      console.log('[DateRangeFilter] Restoring filter from initialDateRange:', initialDateRange.filter);
      // Find the matching option from timeFilterOptions to get the complete object with icon, color, etc.
      const matchedOption = timeFilterOptions.find(opt => opt.id === initialDateRange.filter.id);
      if (matchedOption) {
        console.log('[DateRangeFilter] Matched option from timeFilterOptions:', matchedOption);
        return matchedOption;
      }
      // If it's a custom filter, return the custom filter from initialDateRange
      if (initialDateRange.filter.id === 'Custom') {
        console.log('[DateRangeFilter] Using custom filter from initialDateRange');
        return initialDateRange.filter;
      }
    }
    if (defaultFilterId) {
      const found = timeFilterOptions.find(opt => opt.id === defaultFilterId);
      console.log('[DateRangeFilter] Using defaultFilterId:', defaultFilterId, 'found:', found);
      return found || timeFilterOptions[0];
    }
    const fallback = showAllOption 
      ? timeFilterOptions.find(opt => opt.id === 'All') || timeFilterOptions[0]
      : timeFilterOptions.find(opt => opt.id === 'This Month') || timeFilterOptions[0];
    console.log('[DateRangeFilter] Using fallback filter:', fallback);
    return fallback;
  };

  const [selectedTimeFilter, setSelectedTimeFilter] = useState(() => {
    const resolved = resolveDefaultFilter();
    console.log('[DateRangeFilter] Resolved initial filter:', resolved);
    return resolved;
  });
  
  // Only set custom dates if the filter is actually 'Custom', otherwise clear them
  const [customStart, setCustomStart] = useState(() => {
    if (initialDateRange?.filter?.id === 'Custom') {
      return initialDateRange?.currentStart || null;
    }
    return null;
  });
  const [customEnd, setCustomEnd] = useState(() => {
    if (initialDateRange?.filter?.id === 'Custom') {
      return initialDateRange?.currentEnd || null;
    }
    return null;
  });
  const [customApplied, setCustomApplied] = useState(initialDateRange?.filter?.id === 'Custom');
  const [customDateRange, setCustomDateRange] = useState(
    initialDateRange?.filter?.id === 'Custom' && initialDateRange.filter.dateRange 
      ? initialDateRange.filter.dateRange 
      : ''
  );
  
  console.log('[DateRangeFilter] Initial state - selectedTimeFilter:', selectedTimeFilter, 'initialDateRange:', initialDateRange);
  const [localTeams, setLocalTeams] = useState([]);
  const [selectedTeams, setSelectedTeams] = useState(externalSelectedTeams || []);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [localAgents, setLocalAgents] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState(externalSelectedAgents || []);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);

  // Fetch teams when team filter is enabled
  useEffect(() => {
    const shouldFetchTeams = showTeamFilter || isAgentReportsPage;
    if (shouldFetchTeams && accountId) {
      const fetchTeams = async () => {
        try {
          setLoadingTeams(true);
          const response = await teamService.getTeams(accountId);
          const teamsData = response.data || [];
          setLocalTeams(teamsData);
          if (setTeams) {
            setTeams(teamsData);
          }
        } catch (error) {
          console.error('Error fetching teams:', error);
          setLocalTeams([]);
          if (setTeams) {
            setTeams([]);
          }
        } finally {
          setLoadingTeams(false);
        }
      };
      fetchTeams();
    }
  }, [showTeamFilter, isAgentReportsPage, accountId, setTeams]);

  // Fetch agents when agent filter is enabled or on agent reports page
  useEffect(() => {
    const shouldFetchAgents = showAgentFilter || isAgentReportsPage;
    if (shouldFetchAgents && accountId) {
      const loadAgents = async () => {
        setLoadingAgents(true);
        try {
          const agentData = await fetchAgentSelect();
          setLocalAgents(agentData || []);
          if (setAgents) {
            setAgents(agentData || []);
          }
        } catch (error) {
          console.error('Error fetching agents:', error);
          setLocalAgents([]);
          if (setAgents) {
            setAgents([]);
          }
        } finally {
          setLoadingAgents(false);
        }
      };
      loadAgents();
    }
  }, [showAgentFilter, isAgentReportsPage, accountId, setAgents]);

  // Handle agent selection and navigation (for agent reports page)
  const handleAgentChange = (event, newValue) => {
    setSelectedAgent(newValue);
    if (newValue) {
      const agentId = newValue.agent_id || newValue.id;
      if (agentId) {
        navigate(`/reports/agent/${agentId}`);
      }
    }
  };

  // Sync external selectedTeams with internal state
  useEffect(() => {
    if (externalSelectedTeams !== undefined) {
      setSelectedTeams(externalSelectedTeams);
    }
  }, [externalSelectedTeams]);

  // Sync external selectedAgents with internal state
  useEffect(() => {
    if (externalSelectedAgents !== undefined) {
      setSelectedAgents(externalSelectedAgents);
    }
  }, [externalSelectedAgents]);

  // Auto-trigger onChange with default filter on mount
  useEffect(() => {
    console.log('[DateRangeFilter] useEffect mount - onChange:', !!onChange, 'selectedTimeFilter:', selectedTimeFilter, 'initialDateRange:', initialDateRange);
    
    if (!onChange || !selectedTimeFilter) {
      console.log('[DateRangeFilter] Skipping mount effect - missing onChange or selectedTimeFilter');
      return;
    }

    // If initialDateRange is provided (restored from localStorage or passed from parent), use it
    if (initialDateRange) {
      // Check if the filter ID matches - if it does, we might not need to call onChange
      // But we still need to ensure all_results is correctly set
      const shouldSetAllResults = initialDateRange.all_results === true || selectedTimeFilter.id === 'All';
      
      console.log('[DateRangeFilter] Calling onChange with restored data - filter:', selectedTimeFilter, 'all_results:', shouldSetAllResults);
      // Use selectedTimeFilter (which is already matched to timeFilterOptions) 
      // but keep the date values from initialDateRange
      onChange({
        filter: selectedTimeFilter, // Use the properly matched filter object
        all_results: shouldSetAllResults, // Explicitly calculate all_results
        currentStart: initialDateRange.currentStart,
        currentEnd: initialDateRange.currentEnd,
        compareStart: initialDateRange.compareStart,
        compareEnd: initialDateRange.compareEnd,
        compareLabel: initialDateRange.compareLabel,
      });
      return;
    }
    
    // Otherwise, trigger with the default selected filter
    console.log('[DateRangeFilter] Calling onChange with default selectedTimeFilter:', selectedTimeFilter);
    const ranges = getDateRanges(selectedTimeFilter);
    onChange({
      filter: selectedTimeFilter,
      all_results: selectedTimeFilter.id === 'All' ? true : false,
      ...ranges
    });
  }, []); // Empty dependency array means this runs only once on mount

  // Update selectedTimeFilter when initialDateRange changes (e.g., from URL params)
  useEffect(() => {
    if (initialDateRange?.filter?.id) {
      const matchedOption = timeFilterOptions.find(opt => opt.id === initialDateRange.filter.id);
      if (matchedOption && matchedOption.id !== selectedTimeFilter.id) {
        console.log('[DateRangeFilter] Updating selectedTimeFilter from initialDateRange:', matchedOption);
        setSelectedTimeFilter(matchedOption);
      }
    }
  }, [initialDateRange?.filter?.id, timeFilterOptions, selectedTimeFilter.id]);

  const handleTimeFilterChange = (option) => {
    console.log('DateRangeFilter: handleTimeFilterChange called with option:', option);
    setSelectedTimeFilter(option);
    setCustomApplied(false);
    
    // Reset custom date fields when a preset filter is selected
    setCustomStart(null);
    setCustomEnd(null);
    setCustomDateRange('');

    const ranges = getDateRanges(option);
    if (onChange) {
      onChange({
        filter: option,
        all_results: option.id === 'All' ? true : false,
        ...ranges
      });
    }
  };

  const handleApplyCustom = () => {
    if (customStart && customEnd && !customStart.isAfter(customEnd)) {
      const dateRangeText = `${customStart.format('DD/MM/YY')}-${customEnd.format('DD/MM/YY')}`;
      setCustomDateRange(dateRangeText);
      setCustomApplied(true);

      const customOption = {
        id: 'Custom',
        label: 'Custom',
        dateRange: dateRangeText,
        color: '#dc3545'
      };

      setSelectedTimeFilter(customOption);

      if (onChange) {
        const diff = customEnd.diff(customStart, 'day') + 1;
        const compareEnd = customStart.subtract(1, 'day');
        const compareStart = compareEnd.subtract(diff - 1, 'day');
        onChange({
          filter: customOption,
          all_results: false,
          currentStart: customStart,
          currentEnd: customEnd,
          compareStart,
          compareEnd,
          compareLabel: 'previous period'
        });
      }
    }
  };

  const resetCustom = () => {
    setCustomStart(null);
    setCustomEnd(null);
    setCustomDateRange('');
    setCustomApplied(false);
  };

  const handleTeamChange = (event) => {
    const value = event.target.value;
    setSelectedTeams(typeof value === 'string' ? value.split(',') : value);
    // Notify parent component about team selection change
    if (onTeamProjectFilter) {
      onTeamProjectFilter(typeof value === 'string' ? value.split(',') : value);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', width: '100%' }}>
      {!hideTimeFilters && (
        <>
          {timeFilterOptions.map((option) => (
            <Tooltip key={option.id} title={option.description}>
              <Chip
                icon={option.icon}
                label={
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 50 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem', lineHeight: 1 }}>
                      {option.label}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.6rem', opacity: 0.8, lineHeight: 1 }}>
                      {option.dateRange}
                    </Typography>
                  </Box>
                }
                onClick={() => handleTimeFilterChange(option)}
                sx={{
                  height: 'auto',
                  py: 0.8,
                  px: 1,
                  backgroundColor: selectedTimeFilter.id === option.id 
                    ? `${option.color}15` 
                    : 'background.paper',
                  border: selectedTimeFilter.id === option.id 
                    ? `2px solid ${option.color}` 
                    : '2px solid transparent',
                  color: selectedTimeFilter.id === option.id 
                    ? option.color 
                    : 'text.primary',
                  '&:hover': {
                    backgroundColor: `${option.color}10`,
                    borderColor: option.color,
                    transform: 'translateY(-1px)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                  },
                  transition: 'all 0.2s ease-in-out',
                  cursor: 'pointer',
                  '& .MuiChip-icon': {
                    color: 'inherit',
                    fontSize: '0.9rem'
                  }
                }}
              />
            </Tooltip>
          ))}

          {/* OR Separator */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center'
          }}>
            <Typography 
              variant="caption" 
              sx={{ 
                color: 'text.secondary', 
                fontWeight: 500,
                fontSize: '0.75rem',
                px: 0.5
              }}
            >
              OR
            </Typography>
          </Box>
          
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Start Date"
              value={customStart}
              onChange={setCustomStart}
              maxDate={customEnd || undefined}
              slotProps={{ 
                textField: { 
                  size: 'small', 
                  sx: { 
                    width: 130,
                    '& .MuiInputBase-input': {
                      fontSize: '0.75rem',
                      padding: '6px 8px'
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.75rem'
                    }
                  },
                  placeholder: 'Start Date'
                } 
              }}
              format="DD/MM/YY"
            />
            <DatePicker
              label="End Date"
              value={customEnd}
              onChange={setCustomEnd}
              minDate={customStart || undefined}
              slotProps={{ 
                textField: { 
                  size: 'small', 
                  sx: { 
                    width: 130,
                    '& .MuiInputBase-input': {
                      fontSize: '0.75rem',
                      padding: '6px 8px'
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.75rem'
                    }
                  },
                  placeholder: 'End Date'
                } 
              }}
              format="DD/MM/YY"
            />
          </LocalizationProvider>
          
          <Button
            variant="contained"
            size="small"
            disabled={!customStart || !customEnd || customStart.isAfter(customEnd)}
            onClick={handleApplyCustom}
            sx={{ 
              backgroundColor: 'primary.main',
              '&:hover': { backgroundColor: 'primary.dark' },
              px: 2,
              py: 0.8,
              height: '32px',
              fontSize: '0.75rem'
            }}
          >
            Apply
          </Button>
        </>
      )}

      {/* Agent Search - only on agent reports page */}
      {isAgentReportsPage && (
        <>
          <Box sx={{ flex: 1 }} />
          <Autocomplete
            options={localAgents}
            value={selectedAgent}
            onChange={handleAgentChange}
            loading={loadingAgents}
            getOptionLabel={(option) => option.agent_name || option.name || ''}
            isOptionEqualToValue={(option, value) => 
              (option.agent_id || option.id) === (value?.agent_id || value?.id)
            }
            sx={{
              minWidth: 200,
              maxWidth: { xs: '100%', sm: '250px' },
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                height: '32px',
                fontSize: '0.75rem',
                '&.Mui-focused': {
                  boxShadow: `0 0 0 2px ${alpha('#1976d2', 0.1)}`,
                },
              },
            }}
            renderOption={(props, option) => {
              const teamName = option.team_name === 'Others' || !option.team_name 
                ? 'No Team' 
                : option.team_name;
              
              return (
                <Box
                  component="li"
                  {...props}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    py: 1.5,
                    px: 2,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: alpha('#1976d2', 0.08),
                    },
                    '&[aria-selected="true"]': {
                      backgroundColor: alpha('#1976d2', 0.12),
                    },
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 500,
                      color: 'primary.main',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    {option.agent_name || option.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5 }}>
                    {teamName}
                  </Typography>
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search agent..."
                variant="outlined"
                size="small"
                fullWidth
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: '0.75rem',
                    padding: '6px 8px',
                  },
                }}
              />
            )}
            noOptionsText="No agents found"
          />
        </>
      )}

      {/* Agent Filter - Dashboard and other pages with search support */}
      {showAgentFilter && !isAgentReportsPage && (
        <>
          {!showTeamFilter && <Box sx={{ flex: 1 }} />}
          <Autocomplete
            multiple
            options={localAgents}
            value={localAgents.filter(agent => 
              selectedAgents.includes(agent.agent_id || agent.id)
            )}
            onChange={(event, newValue) => {
              const agentIds = newValue.map(agent => agent.agent_id || agent.id);
              setSelectedAgents(agentIds);
              if (onAgentFilter) {
                onAgentFilter(agentIds);
              }
            }}
            disabled={loadingAgents || (selectedTeams && selectedTeams.length > 0)}
            loading={loadingAgents}
            getOptionLabel={(option) => option.agent_name || option.name || ''}
            isOptionEqualToValue={(option, value) => 
              (option.agent_id || option.id) === (value?.agent_id || value?.id)
            }
            renderTags={(value, getTagProps) => {
              if (value.length === 0) return null;
              if (value.length === 1) {
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <FilterIcon sx={{ fontSize: '0.875rem', color: '#1976d2' }} />
                    <Typography sx={{ fontSize: '0.75rem', color: '#1976d2' }}>
                      {value[0].agent_name || value[0].name}
                    </Typography>
                  </Box>
                );
              }
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <FilterIcon sx={{ fontSize: '0.875rem', color: '#1976d2' }} />
                  <Typography sx={{ fontSize: '0.75rem', color: '#1976d2' }}>
                    {value.length} Agents
                  </Typography>
                </Box>
              );
            }}
            renderOption={(props, option) => {
              const agentName = option.agent_name || option.name;
              const teamName = option.team_name || '';
              return (
                <Box
                  component="li"
                  {...props}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    py: 1,
                    px: 2,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: alpha('#1976d2', 0.08),
                    },
                    '&[aria-selected="true"]': {
                      backgroundColor: alpha('#1976d2', 0.12),
                    },
                  }}
                >
                  <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                    {agentName}
                  </Typography>
                  {teamName && (
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                      {teamName}
                    </Typography>
                  )}
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={selectedAgents.length === 0 ? "Search agents..." : ""}
                variant="outlined"
                size="small"
                sx={{
                  minWidth: 180,
                  '& .MuiInputBase-root': {
                    borderRadius: 2,
                    fontSize: '0.75rem',
                    backgroundColor: '#e3f2fd',
                    borderColor: '#2196f3',
                    color: '#1976d2',
                    height: '32px',
                    paddingTop: '0px',
                    paddingBottom: '0px',
                    '&:hover': {
                      backgroundColor: '#bbdefb',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2196f3',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#1976d2',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#1976d2',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: '#f5f5f5',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#e0e0e0',
                      },
                    },
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '0.75rem',
                    padding: '4px 8px',
                    height: 'auto',
                  },
                  '& .MuiAutocomplete-tag': {
                    display: 'none',
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: selectedAgents.length === 0 ? (
                    <FilterIcon sx={{ fontSize: '0.875rem', color: '#1976d2', ml: 1 }} />
                  ) : params.InputProps.startAdornment,
                }}
              />
            )}
            noOptionsText="No agents found"
            sx={{
              minWidth: 180,
            }}
          />
        </>
      )}

      {/* Team Filter and Export PDF */}
      {(showTeamFilter || isAgentReportsPage) && (
        <>
          {!isAgentReportsPage && !showAgentFilter && <Box sx={{ flex: 1 }} />}
          <FormControl 
            size="small" 
            sx={{ 
              minWidth: 140,
            }}
          >
            <Select
              labelId="teams-select-label"
              id="teams-select"
              multiple
              value={selectedTeams}
              onChange={handleTeamChange}
              displayEmpty
              disabled={loadingTeams || (selectedAgents && selectedAgents.length > 0)}
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <FilterIcon sx={{ fontSize: '0.875rem', color: '#2e7d32' }} />
                      <Typography sx={{ fontSize: '0.75rem', color: '#2e7d32' }}>
                        Teams
                      </Typography>
                    </Box>
                  );
                }
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    <FilterIcon sx={{ fontSize: '0.875rem', color: '#2e7d32' }} />
                    {selected.length === 1 ? (
                      <Typography sx={{ fontSize: '0.75rem', color: '#2e7d32' }}>
                        {(() => {
                          const team = localTeams.find(t => t.id === selected[0] || t.id?.toString() === selected[0]?.toString());
                          return team ? team.name : selected[0];
                        })()}
                      </Typography>
                    ) : (
                      <Typography sx={{ fontSize: '0.75rem', color: '#2e7d32' }}>
                        {selected.length} Teams
                      </Typography>
                    )}
                  </Box>
                );
              }}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: 2,
                py: 0.8,
                height: '32px',
                fontSize: '0.75rem',
                backgroundColor: '#e8f5e8',
                borderColor: '#4caf50',
                color: '#2e7d32',
                '&:hover': {
                  backgroundColor: '#c8e6c9',
                  borderColor: '#388e3c',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#4caf50',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#388e3c',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#388e3c',
                },
                '& .MuiSelect-icon': {
                  color: '#2e7d32',
                },
                '&.Mui-disabled': {
                  backgroundColor: '#f5f5f5',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e0e0e0',
                  },
                },
              }}
            >
              {localTeams.length > 0 ? (
                localTeams.map((team) => (
                  <MenuItem key={team.id} value={team.id}>
                    {team.name}
                  </MenuItem>
                ))
              ) : (
                !loadingTeams && (
                  <MenuItem disabled>No teams available</MenuItem>
                )
              )}
            </Select>
          </FormControl>
          {(showExportPDF || (isAgentReportsPage && onExportPDF)) && (
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={onExportPDF}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: 2,
                py: 0.8,
                height: '32px',
                fontSize: '0.75rem',
                backgroundColor: '#2e7d32',
                '&:hover': {
                  backgroundColor: '#1b5e20',
                },
              }}
            >
              Export PDF
            </Button>
          )}
        </>
      )}
    </Box>
  );
};

export default DateRangeFilter;
